/**
 * Ein Datumsfeld der Bestellung, das erst beim Verlassen des Feldes (`onBlur`) committet - statt
 * bei jedem `onChange`.
 *
 * ================= Root Cause (Asana-Task 1218053584680823) =================
 *
 * „Datum ändern geht nicht wenn einmal auf Jahr 0000 (Beispiel BE-10465) - Bei Eingabe kommt immer
 * Fehler - Bestellung kann nicht geändert werden". Jedes der fuenf betroffenen Datumsfelder rief
 * bisher bei JEDEM `onChange` sofort `onAendern(...)`/`onRate(...)` auf - ungebremst, ohne
 * Formatpruefung (weder Frontend noch Backend). Ein `<input type="date">` feuert beim Tippen des
 * Jahres PRO SEGMENT ein Event: React behandelt `date` als textartiges Feld (`supportedInputTypes`
 * in `react-dom`) und loest `onChange` ueber das native `input`-Event aus, nicht erst bei `change`.
 * Ein Zwischenzustand (z. B. ein unvollstaendiges/ungueltiges Jahr wie "0000") ging dadurch SOFORT
 * an den Server, dort ungeprueft bis zur Postgres-`date`-Spalte durchgereicht, die wirft. Weil der
 * Schreibvorgang schon unterwegs war, blieb das Feld haengen.
 *
 * Exakt dasselbe Fehlermuster wurde in diesem Repo fuer Zahlenfelder bereits behoben: `Zahlenfeld`
 * in `PaymentTab/Ratenplan.tsx` (Kopfkommentar „NACHBESSERUNG Task 7"). `Datumsfeld` ist dessen
 * woertliche Uebertragung auf `type="date"` - derselbe Mechanismus, nur ohne die `min`/`max`/`step`-
 * Requisiten, die dort nur fuer Zahlen Sinn ergeben:
 *
 * - lokaler State (`useState`) fuer den angezeigten Wert
 * - `fokussiert`-Ref: `onFocus` setzt ihn `true`
 * - `onInput` aktualisiert NUR den lokalen State (kein Commit)
 * - `onChange={() => {}}` (React verlangt einen Handler bei `value`-kontrolliertem Input)
 * - `onBlur`: `fokussiert.current = false`; `onCommit(lokal)` NUR wenn `lokal !== wert` (echte
 *   Aenderung)
 * - `useEffect`, das den lokalen Wert nur nachzieht, wenn `!fokussiert.current` - so ueberschreibt
 *   ein Nachladen von aussen (z. B. durch ein anderes, bereits verlassenes Feld ausgeloest) nie eine
 *   laufende Eingabe in DIESEM Feld.
 *
 * Gemeinsam importierbar angelegt (statt als lokales Funktions-Component in einer der Zieldateien
 * wie `Zahlenfeld`/`FreitextFeld`, die nur an je einer Stelle gebraucht werden): `Datumsfeld` wird
 * von vier verschiedenen Dateien an fuenf Stellen verwendet (`MainTab/BestellungTab.tsx`,
 * `MainTab/Termine.tsx`, `PaymentTab/Ratenplan.tsx`, `ShipTab/Transport.tsx`, `CalcTab/EkTab.tsx`).
 */
import { useEffect, useRef, useState } from "react";

export interface DatumsfeldProps {
  /** Der zuletzt bestaetigte (gespeicherte) Wert, ISO `YYYY-MM-DD` oder leer. */
  wert: string;
  /** Feuert genau einmal pro abgeschlossener Eingabe, nur bei tatsaechlicher Aenderung. */
  onCommit: (wert: string) => void;
  className?: string;
  disabled?: boolean;
  /** Tooltip - genutzt fuer die Feldzustand-Optik (Sperrgrund), siehe `Drawer/ShipTab/Transport.tsx`. */
  title?: string;
}

export function Datumsfeld({ wert, onCommit, className, disabled, title }: DatumsfeldProps) {
  const [lokal, setLokal] = useState(wert);
  const fokussiert = useRef(false);

  useEffect(() => {
    if (!fokussiert.current) setLokal(wert);
  }, [wert]);

  return (
    <input
      type="date"
      className={className}
      disabled={disabled}
      title={title}
      value={lokal}
      onFocus={() => {
        fokussiert.current = true;
      }}
      onInput={(e) => setLokal(e.currentTarget.value)}
      onChange={() => {}}
      onBlur={() => {
        fokussiert.current = false;
        if (lokal !== wert) onCommit(lokal);
      }}
    />
  );
}
